import { AnimationPicker } from '@/components/admin/experiences/page-editor/animation-picker';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { SPEED_LABEL, tempsSummary } from '@/lib/page-editor';
import type { BlockAnimation, ExperiencePageDraft } from '@matter_extractor/shared';
import { Button, Select } from '@pms-digital/design-system';

/**
 * L'onglet **Animation** d'un bloc (refonte modulaire §9.3; planche
 * `EditeurPageTelephone`; issue #58).
 *
 * « L'animation d'entrée par icônes, la rapidité EN LECTURE - "rapide, héritée
 * du temps 1", avec le lien "Régler le temps 1" - et le sélecteur "Apparaît au
 * temps", qui reste le geste sûr à côté du glisser-déposer. »
 *
 * The speed is read-only here and that is the sentence of H27 rendered: « un
 * bloc ne force pas sa rapidité : elle est celle de son temps ». A control here
 * would be a second place to set one number, and the two would disagree for
 * whoever looks at the pile.
 *
 * A Son carries only « Apparaît au temps » (§9.3: « un son n'en a pas »), which
 * is why the picker is behind a branch rather than greyed: a greyed row of
 * eight icons asks what it would do if it were enabled.
 */

export interface AnimationPanelProps {
  readonly page: ExperiencePageDraft;
  readonly blockId: string;
  readonly animation: BlockAnimation;
  readonly tempsId: string;
  readonly readOnly: boolean;
  /** A Son has no entry to play (§9.3). */
  readonly animated: boolean;
  readonly onAnimation: (next: BlockAnimation) => void;
  readonly onTemps: (tempsId: string) => void;
  readonly onOpenTemps: (tempsId: string) => void;
}

export function AnimationPanel({
  page,
  blockId,
  animation,
  tempsId,
  readOnly,
  animated,
  onAnimation,
  onTemps,
  onOpenTemps,
}: AnimationPanelProps) {
  const index = page.temps.findIndex((one) => one.id === tempsId);
  const temps = page.temps[index] ?? null;

  return (
    <div className="space-y-3">
      {animated ? (
        <div className="space-y-1.5">
          <p className="block font-semibold text-gray-800 text-caption">Animation d’entrée</p>
          <AnimationPicker
            label={`Animation d’entrée du bloc ${blockId}`}
            value={animation}
            readOnly={readOnly}
            onChange={onAnimation}
          />
          <p className="text-gray-600 text-caption leading-4">
            Un clic rejoue l’animation dans l’aperçu.
          </p>
        </div>
      ) : (
        <p className="text-gray-600 text-caption leading-4">
          Un son n’a pas d’animation d’entrée : il part au premier toucher sur la page.
        </p>
      )}

      {temps === null ? null : (
        <div className="space-y-1.5">
          <p className="block font-semibold text-gray-800 text-caption">Rapidité</p>
          <p className="text-gray-800 text-small">
            <span className="font-semibold">{SPEED_LABEL[temps.speed]}</span>, héritée du temps{' '}
            {index + 1}
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenTemps(temps.id)}>
            Régler le temps {index + 1}
          </Button>
          <p className="text-gray-600 text-caption leading-4">
            La rapidité appartient au temps : tous ses blocs bougent au même tempo, chacun avec son
            mouvement.
          </p>
        </div>
      )}

      {/* The test id on a wrapper: the package `Select` takes none, its trigger
          is the `combobox` inside. A select has no read-only state: `disabled`. */}
      <div data-testid={PAGE_EDITOR_IDS.blockTemps}>
        <Select
          label="Apparaît au temps"
          compact
          value={tempsId}
          disabled={readOnly}
          onValueChange={onTemps}
          options={page.temps.map((one, at) => ({ value: one.id, label: tempsSummary(one, at) }))}
          help="Glisser le bloc sur un séparateur de la pile fait le même geste."
        />
      </div>
    </div>
  );
}
